import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { PublicBookingApp } from './public/PublicBookingApp';
import { isBookingHost } from './openslot/helpers';
import { AuthProvider } from './auth/AuthProvider';
import { RequireAuth } from './auth/RequireAuth';
import { RequireAdmin } from './auth/RequireAdmin';
import { ConfigProvider } from './config/ConfigProvider';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { MediaLibraryPage } from './pages/MediaLibraryPage';
import { BillingPage } from './pages/billing/BillingPage';
import { GeneralSettingsPage } from './pages/GeneralSettingsPage';
import { ConnectionsPage } from './pages/ConnectionsPage';
import { BillingSettingsPage } from './pages/BillingSettingsPage';
import { OpenSlotSettingsPage } from './pages/OpenSlotSettingsPage';
import { ConsultingSettingsPage } from './pages/ConsultingSettingsPage';
import { HealthSettingsPage } from './pages/HealthSettingsPage';
import { AiSettingsPage } from './pages/AiSettingsPage';
import { MaxsoldSettingsPage } from './pages/MaxsoldSettingsPage';
import { AiChatPage } from './pages/ai/AiChatPage';
import { OuraRingPage } from './pages/health/OuraRingPage';
import { GolfActivityPage } from './pages/health/golf/GolfActivityPage';
import { GolfImportPage } from './pages/health/golf/GolfImportPage';
import { GolfActivitySummaryPage } from './pages/health/golf/GolfActivitySummaryPage';
import { GolfTeeTimesPage } from './pages/health/golf/GolfTeeTimesPage';
import { GolfScoresPage } from './pages/health/golf/GolfScoresPage';
import { GolfScoreDetailPage } from './pages/health/golf/GolfScoreDetailPage';
import { GolfStatsPage } from './pages/health/golf/GolfStatsPage';
import { BillingTimeOverviewPage } from './pages/consulting/BillingTimeOverviewPage';
import { BillingTimeTimerPage } from './pages/consulting/BillingTimeTimerPage';
import { ClientsPage } from './pages/consulting/ClientsPage';
import { ProjectsPage } from './pages/consulting/ProjectsPage';
import { TeamPage } from './pages/consulting/TeamPage';
import { ReportsPage } from './pages/consulting/ReportsPage';
import { InvoicesPage } from './pages/consulting/InvoicesPage';
import { MaxsoldFavouritesPage } from './pages/maxsold/MaxsoldFavouritesPage';
import { MaxsoldSearchPage } from './pages/maxsold/MaxsoldSearchPage';
import { MaxsoldAuctionPage } from './pages/maxsold/MaxsoldAuctionPage';
import { MaxsoldLotPage } from './pages/maxsold/MaxsoldLotPage';
import { MaxsoldBidsPage } from './pages/maxsold/MaxsoldBidsPage';
import { MaxsoldInvoicesPage } from './pages/maxsold/MaxsoldInvoicesPage';
import { CloudDnsPage } from './pages/cloud/CloudDnsPage';
import { CloudDnsZonePage } from './pages/cloud/CloudDnsZonePage';
import { CloudHealthChecksPage } from './pages/cloud/CloudHealthChecksPage';
import { CloudHealthCheckDetailPage } from './pages/cloud/CloudHealthCheckDetailPage';
import { BillingTimeStubPage } from './pages/consulting/BillingTimeStubPage';
import { BILLING_TIME_ITEMS } from './pages/consulting/billingTimeNav';
import { EventTypesPage } from './pages/openslot/EventTypesPage';
import { EventTypeFormPage } from './pages/openslot/EventTypeFormPage';
import { AvailabilityPage } from './pages/openslot/AvailabilityPage';
import { TimeOffPage } from './pages/openslot/TimeOffPage';
import { BookingsPage } from './pages/openslot/BookingsPage';

export function App() {
  // The public booking hosts (book.* and mymtg.io) serve a login-free booking app from the same SPA.
  if (typeof window !== 'undefined' && isBookingHost(window.location.hostname)) {
    return <PublicBookingApp />;
  }

  return (
    <AuthProvider>
      <ConfigProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />

            {/* Authenticated area (provides /api/me via MeProvider) */}
            <Route element={<RequireAuth />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/ai" element={<AiChatPage />} />
              <Route path="/ai/:conversationId" element={<AiChatPage />} />
              <Route path="/openslot" element={<Navigate to="/openslot/scheduling" replace />} />
              <Route path="/openslot/scheduling" element={<EventTypesPage />} />
              <Route path="/openslot/scheduling/:id" element={<EventTypeFormPage />} />
              <Route path="/openslot/availability" element={<AvailabilityPage />} />
              <Route path="/openslot/time-off" element={<TimeOffPage />} />
              <Route path="/openslot/bookings" element={<BookingsPage />} />
              <Route path="/consulting" element={<Navigate to="/consulting/billing-time" replace />} />
              <Route path="/consulting/billing-time" element={<Navigate to="/consulting/billing-time/overview" replace />} />
              <Route path="/consulting/billing-time/overview" element={<BillingTimeOverviewPage />} />
              <Route path="/consulting/billing-time/timer" element={<BillingTimeTimerPage />} />
              <Route path="/consulting/billing-time/clients" element={<ClientsPage />} />
              <Route path="/consulting/billing-time/projects" element={<ProjectsPage />} />
              <Route path="/consulting/billing-time/team" element={<TeamPage />} />
              <Route path="/consulting/billing-time/reports" element={<ReportsPage />} />
              <Route path="/consulting/billing-time/invoices" element={<InvoicesPage />} />
              {BILLING_TIME_ITEMS.filter(
                (i) =>
                  i.path !== '/consulting/billing-time/overview' &&
                  i.path !== '/consulting/billing-time/timer' &&
                  i.path !== '/consulting/billing-time/clients' &&
                  i.path !== '/consulting/billing-time/projects' &&
                  i.path !== '/consulting/billing-time/team' &&
                  i.path !== '/consulting/billing-time/reports' &&
                  i.path !== '/consulting/billing-time/invoices',
              ).map((i) => (
                <Route key={i.path} path={i.path} element={<BillingTimeStubPage title={i.label} icon={i.icon} />} />
              ))}
              <Route path="/health" element={<Navigate to="/health/golf" replace />} />
              <Route path="/health/oura" element={<OuraRingPage />} />
              <Route path="/health/golf" element={<Navigate to="/health/golf/scores" replace />} />
              <Route path="/health/golf/scores" element={<GolfScoresPage />} />
              <Route path="/health/golf/scores/:id" element={<GolfScoreDetailPage />} />
              <Route path="/health/golf/stats" element={<GolfStatsPage />} />
              <Route path="/health/golf/activity" element={<GolfActivityPage />} />
              <Route path="/health/golf/activity/:id" element={<GolfActivitySummaryPage />} />
              <Route path="/health/golf/tee-times" element={<GolfTeeTimesPage />} />
              <Route path="/health/golf/import" element={<GolfImportPage />} />
              <Route path="/maxsold" element={<Navigate to="/maxsold/favourites" replace />} />
              <Route path="/maxsold/favourites" element={<MaxsoldFavouritesPage />} />
              <Route path="/maxsold/search" element={<MaxsoldSearchPage />} />
              <Route path="/maxsold/auction/:auctionId" element={<MaxsoldAuctionPage />} />
              <Route path="/maxsold/lot/:lotId" element={<MaxsoldLotPage />} />
              <Route path="/maxsold/bids" element={<MaxsoldBidsPage />} />
              <Route path="/maxsold/invoices" element={<MaxsoldInvoicesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/media" element={<Navigate to="/media/images" replace />} />
              <Route
                path="/media/images"
                element={
                  <RequireAdmin>
                    <MediaLibraryPage />
                  </RequireAdmin>
                }
              />
              <Route path="/cloud" element={<Navigate to="/cloud/dns" replace />} />
              <Route
                path="/cloud/dns"
                element={
                  <RequireAdmin>
                    <CloudDnsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/cloud/dns/:provider/:zoneId"
                element={
                  <RequireAdmin>
                    <CloudDnsZonePage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/cloud/health-checks"
                element={
                  <RequireAdmin>
                    <CloudHealthChecksPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/cloud/health-checks/:id"
                element={
                  <RequireAdmin>
                    <CloudHealthCheckDetailPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/billing"
                element={
                  <RequireAdmin>
                    <BillingPage />
                  </RequireAdmin>
                }
              />
              <Route path="/billing/google-cloud" element={<Navigate to="/billing" replace />} />
              <Route path="/configuration" element={<Navigate to="/configuration/general" replace />} />
              <Route
                path="/configuration/general"
                element={
                  <RequireAdmin>
                    <GeneralSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/connections"
                element={
                  <RequireAdmin>
                    <ConnectionsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/billing"
                element={
                  <RequireAdmin>
                    <BillingSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/openslot"
                element={
                  <RequireAdmin>
                    <OpenSlotSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/consulting"
                element={
                  <RequireAdmin>
                    <ConsultingSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/health"
                element={
                  <RequireAdmin>
                    <HealthSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/ai"
                element={
                  <RequireAdmin>
                    <AiSettingsPage />
                  </RequireAdmin>
                }
              />
              <Route
                path="/configuration/maxsold"
                element={
                  <RequireAdmin>
                    <MaxsoldSettingsPage />
                  </RequireAdmin>
                }
              />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ConfigProvider>
    </AuthProvider>
  );
}
